/**
 * One SSE connection per BROWSER, not per tab.
 *
 * Six tabs of the same user used to hold six connections against a server cap
 * of five, so every new tab evicted the oldest one and the evicted tab
 * reconnected three seconds later — a permanent loop. Here exactly one tab
 * holds an exclusive Web Lock and owns the connection; the others listen on a
 * BroadcastChannel of the same name and receive every event the leader gets.
 *
 * Failover is what the Web Lock buys: closing the tab, crashing it or
 * navigating away releases the lock and the next queued tab takes over. A tab
 * that is merely frozen keeps the lock, so followers also watch for silence
 * and steal the lock when the leader stops proving it is alive.
 *
 * "Alive" means the leader's JS thread is still ticking, which is NOT the same
 * as its stream being up. A leader stuck in its own auth-refresh retry loop
 * (e.g. this tab's session died and every tab shares the browser's dead
 * cookie) keeps ticking the keepalive forever while its `connected` state
 * stays false — every other tab then sits behind a leader that can never
 * recover, however long they wait. The leader tracks its own `connected`
 * state and voluntarily gives up the lock once it has stayed disconnected
 * past `LEADER_GIVE_UP_MS`, so a fresh acquire (by this tab or another) gets
 * a real shot at reconnecting instead of waiting on a corpse that still waves.
 *

 * A browser without Web Locks or BroadcastChannel (pre-15.4 Safari) keeps the
 * old shape: every tab runs its own connection.
 */

export type SseTabMessage =
    | { kind: 'event'; type: string; data: string }
    | { kind: 'state'; connected: boolean }

type ChannelMessage = SseTabMessage | { kind: 'hello' } | { kind: 'alive' }

export interface TabLeaderOptions {
    /** Lock and channel name. One name per stream URL. */
    key: string
    /** Opens the real connection. Called only while this tab is the leader. */
    openStream: (publish: (message: SseTabMessage) => void) => {
        close: () => void
    }
    /** Every event and state change, whether produced or received. */
    onMessage: (message: SseTabMessage) => void
}

export interface TabLeaderHandle {
    close: () => void
}

// Proof of life on the channel. Three missed beats and a follower takes over:
// a frozen tab keeps its lock but its timers stop firing.
const KEEPALIVE_MS = 20_000
const FOLLOWER_TIMEOUT_MS = 60_000
// Comfortably past one full self-heal cycle of the resilient stream (its own
// backoff + auth-refresh retries cap around a minute), so this never fires on
// an ordinary reconnect blip — only on a leader that has given up recovering
// on its own.
const LEADER_GIVE_UP_MS = 90_000

function getLockManager(): LockManager | null {
    if (typeof navigator === 'undefined') return null
    return (navigator.locks as LockManager | undefined) ?? null
}

export function createTabLeader({
    key,
    openStream,
    onMessage,
}: TabLeaderOptions): TabLeaderHandle {
    const locks = getLockManager()
    if (!locks || typeof BroadcastChannel === 'undefined') {
        const stream = openStream(onMessage)
        return { close: () => stream.close() }
    }

    const channel = new BroadcastChannel(key)
    let closed = false
    let stream: { close: () => void } | null = null
    let lastState: SseTabMessage | null = null
    let releaseLock: (() => void) | null = null
    let abort: AbortController | null = null
    let keepalive: ReturnType<typeof setInterval> | null = null
    let watchdog: ReturnType<typeof setTimeout> | null = null
    // Set the instant this tab's OWN stream last turned (or stayed)
    // disconnected; null while connected. Read by the keepalive tick to decide
    // whether this leader has earned the right to keep the lock.
    let disconnectedSince: number | null = null

    const publish = (message: SseTabMessage) => {
        if (message.kind === 'state') {
            lastState = message
            disconnectedSince = message.connected
                ? null
                : (disconnectedSince ?? Date.now())
        }
        channel.postMessage(message)
        onMessage(message)
    }

    const armWatchdog = () => {
        if (watchdog) clearTimeout(watchdog)
        watchdog = null
        if (closed || stream) return
        watchdog = setTimeout(() => {
            if (closed || stream) return
            acquire(true)
        }, FOLLOWER_TIMEOUT_MS)
    }

    const becomeLeader = () => {
        if (watchdog) clearTimeout(watchdog)
        watchdog = null
        // Disconnected until `openStream` proves otherwise: a leader whose
        // stream never comes up must still be timed from the moment it took
        // the lock, not sit exempt forever for lack of a state message.
        disconnectedSince = Date.now()
        stream = openStream(publish)
        keepalive = setInterval(() => {
            if (
                disconnectedSince !== null &&
                Date.now() - disconnectedSince >= LEADER_GIVE_UP_MS
            ) {
                giveUpLeadership()
                return
            }
            channel.postMessage({ kind: 'alive' })
        }, KEEPALIVE_MS)
    }

    const stopLeading = () => {
        if (keepalive) clearInterval(keepalive)
        keepalive = null
        stream?.close()
        stream = null
        lastState = null
        disconnectedSince = null
    }

    // This tab's own stream has been down longer than any normal self-heal
    // cycle takes. Release the lock and re-queue behind whoever is next —
    // possibly this same tab again, but with a fresh connection attempt.
    const giveUpLeadership = () => {
        stopLeading()
        releaseLock?.()
        armWatchdog()
        acquire(false)
    }

    function acquire(steal: boolean) {
        if (closed) return
        const held = new Promise<void>((resolve) => {
            releaseLock = resolve
        })
        abort = steal ? null : new AbortController()
        // `steal` may not travel with a signal, and a plain request needs no
        // timeout: it simply queues until the current leader lets go.
        const options: LockOptions = steal
            ? { mode: 'exclusive', steal: true }
            : { mode: 'exclusive', signal: abort!.signal }
        locks!
            .request(key, options, async () => {
                if (closed) return
                becomeLeader()
                await held
            })
            .catch(() => {
                // Aborted by close(), or this tab's lock was stolen because a
                // follower judged it dead. Either way it is no longer leader.
                if (closed) return
                stopLeading()
                armWatchdog()
                acquire(false)
            })
    }

    channel.onmessage = (event: MessageEvent) => {
        const message = event.data as ChannelMessage | null
        if (!message || closed) return
        if (message.kind === 'hello') {
            if (stream && lastState) channel.postMessage(lastState)
            return
        }
        if (stream) return
        armWatchdog()
        if (message.kind === 'alive') return
        onMessage(message)
    }

    armWatchdog()
    acquire(false)
    // Ask whoever leads for the current state; a tab must not show "offline"
    // just because it joined after the connection came up.
    channel.postMessage({ kind: 'hello' })

    return {
        close: () => {
            closed = true
            if (watchdog) clearTimeout(watchdog)
            watchdog = null
            stopLeading()
            releaseLock?.()
            abort?.abort()
            channel.close()
        },
    }
}
